import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, Select } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchFinanceDetailRefreshAsync } from "containers/finance-detail/finance-detail-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import CustomerContactMultiSelect from "features/customer-contact-select/CustomerContactMultiSelect";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import { uploadToDokobit } from "services/upload-file.service";
import { signerEditErrorKey } from "utils/signer-edit-errors";
import { fetchFileDetails } from "containers/file/file.Slice";
import UserSelect from "features/user-select/user-select";
import { useParams } from "react-router-dom";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";

interface ConfirmModalProps {
  companyId: string;
  customerId: string;
  fileId?: string;
  relatedTo?: string;
  year?: number;
  financeId?: string;
  // Runs after the request was sent, e.g. so a list outside redux can refresh.
  onSuccess?: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  customerId,
  fileId,
  relatedTo,
  year,
  financeId,
  onSuccess,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { customer } = useSelector((state: any) => state.customerReducer);
  const { t } = useTranslation();
  const { orderId } = useParams();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>(
    customer?.assignedUser?.id || ""
  );
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [signType, setSignType] = useState("Dokobit");
  const [selectedNonTechUser, setSelectedNonTechUser] = useState(
    customer?.assignedUser?.id || ""
  );

  const handleDokobitFile = async () => {
    try {
      setIsLoading(true);
      const payload = {
        fileId,
        contactSigners: Array.from(new Set(selectedContactIds)),
        companyUserSigner: selectedUser,
        signType,
        nontechuser: selectedNonTechUser,
      };
      await uploadToDokobit(companyId, payload);
      if (relatedTo) {
        dispatch(fetchFileDetails({ fileId: relatedTo, companyId }));
      } else if (fileId && companyId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
      }

      dispatch(fetchCustomer({ customerId, companyId: companyId! }));

      // Refresh finance detail if year is provided
      if (year) {
        dispatch(fetchFinanceDetailRefreshAsync({
          companyId,
          customerId,
          year,
          financeId,
        }));
      }

      if (companyId && orderId) {
        dispatch(fetchOrderDetails({ companyId, orderId }));
      }
      onSuccess?.();
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e: any) {
      setErrMsg(signerEditErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const signCollection = [
    { label: t("manual_sign"), value: "Manual" },
    { label: "Dokobit", value: "Dokobit" },
  ];

  const handleSignType = (value: string) => {
    setSignType(value);
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_request_to_sign_file")}
      </h1>
      <div style={{ marginBottom: "10px" }}>
        <CustomerContactMultiSelect
          label={"select_contact"}
          onChange={setSelectedContactIds}
          value={selectedContactIds}
          companyId={companyId}
          customerId={customerId}
          isRequired={true}
        />

        <UserSelectByPermissions
          value={selectedUser}
          onChange={setSelectedUser}
          isRequired={true}
          Permission={"Signature"}
        />

        <UserSelect
          onChange={setSelectedNonTechUser}
          value={selectedNonTechUser}
          label={"select_user"}
        />
        <Select
          label={t("sign_type")}
          options={signCollection}
          value={signType}
          onChange={handleSignType}
          requiredIndicator={true}
        />
      </div>
      <InlineStack align="space-between">
        <Button
          onClick={handleDokobitFile}
          loading={isLoading}
          disabled={!selectedUser || selectedContactIds.length === 0}
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
